import React from 'react';

import PropTypes from 'prop-types';
import { createSelector } from 'reselect';
import styled from 'styled-components';

import { generateObjectId } from '@builttocreate/engine-utils';

import FieldDisplayTypes from '../../../../constants/FieldDisplayTypes';
import FieldTypes from '../../../../constants/FieldTypes';
import FieldModes from '../../../../constants/FieldModes';
import ChangelogTypes from '../../../../constants/ChangelogTypes';
import CollectionRenderer from '../collectionRenderer/CollectionRenderer';
import FieldTitle from '../FieldTitle';
import FieldDecorators from '../FieldDecorators';
import InputGroupFieldRow from '../table/InputGroupFieldRow';
import TableFieldFooter from '../table/TableFieldFooter';
import TableFieldHeader from '../table/TableFieldHeader';
import TableFieldRow from '../table/TableFieldRow';
import TableFieldRteHeader from '../table/TableFieldRteHeader';
import TableFieldRteRow from '../table/TableFieldRteRow';
import Tooltip from '../../../misc/Tooltip';

import { flattenArray, getNestedItem  } from '../../../../lib/collectionHelper';
import { filterHiddenTableColumnsSelector, getDecoratorColumnWidth } from '../../../../lib/tableHelper';
import { isRowPopulatedForRte } from '../../../../lib/rteTableHelper';
import DecoratorConfigContext from '../../../DecoratorConfigContext';

import TableFieldRteEditRowModal from '../table/TableFieldRteEditRowModal';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}

  /*
   * Only use height when you don't need height 
   * measurements of the table field.
   */
  ${(props) => {
    return props.measure ? '' : 'height: 100%;';
  }}
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right:0;
  top:0;
`;

const StyledFieldHeader = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 6px;
  padding-bottom: 4px;
  padding-right: 20px;
`;

class CollectionField extends React.Component {

  static propTypes = {

    fieldMode: PropTypes.string,
    type: PropTypes.string,
    displayType: PropTypes.string,
    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,

    disabled: PropTypes.bool,
    measure: PropTypes.bool,
    enableSystemFilePicker: PropTypes.bool,

    //Property used by PDF Renderer
    layouts: PropTypes.array,

    schema: PropTypes.object,
    value: PropTypes.array,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Column title styles
    columnTitleFontSize: PropTypes.number,
    columnTitleFontColor: PropTypes.string,
    columnTitleTextAlign: PropTypes.string,
    columnTitleFontStyle: PropTypes.string,
    columnTitleFontWeight: PropTypes.string,
    columnTitleTextTransform: PropTypes.string,
    columnTitleTextDecoration: PropTypes.string,
    columnTitleBackgroundColor: PropTypes.string,
    columnTitlePadding: PropTypes.number,

    //Cell styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    borderRadius: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    height: PropTypes.number,

    decorators: PropTypes.array,
    rowDecorators: PropTypes.array,
    onDecoratorClick: PropTypes.func,

  }

  static contextType = DecoratorConfigContext;

  constructor(props) {
    super(props);

    this.state = {
      focused: null,
      rowHeights: {},
      allColumnWidthsLoaded: false,
      editRowModalOpen: false,
      editRowModalRow: null,
      editRowModalSchemaKey: null,
    };

    this.columnWidths = {};
    this.measuredRows = {};

    this._getFlattenRowsSelector = createSelector(
      (state) => state.type,
      (state) => state.displayType,
      (state) => state.fieldPositionSchema,
      (state) => state.schema,
      (state) => state.value,
      (state) => state.disabled,
      (state) => state.fieldMode,
      (state) => state.view,
      (state) => state.docFields,
      flattenArray
    );

    this._getFilteredFlattenedRowsSelector = createSelector(
      (state) => state.items,
      (state) => state.layouts,
      (items, layouts) => {

        const layoutLookup = layouts.reduce((acc, layout) => {
          acc[layout.key] = layout;
          return acc;
        }, {});

        return items.filter((item) => layoutLookup[item.layoutKey]);

      }
    );

    this._getHeaderRowsSelectorForColumnWidthsCapture = createSelector(
      (state) => state.schema,
      (state) => state.fieldPositionSchema,
      (state) => state.fieldMode,
      (schema, fieldPositionSchema, fieldMode) => {

        const schemaKeys = Object.keys(schema);
        const flattenedArray = [];

        function getSchemaDepth(schema, targetSchemaId) {
          function dfs(currentKey, currentDepth) {
            if (currentKey === targetSchemaId) return currentDepth;
            const children = schema[currentKey]?.children || [];
            for (let i = 0; i < children.length; i++) {
              const found = dfs(children[i], currentDepth + 1);
              if (found !== -1) return found;
            }
            return -1;
          }


          let rootSchemaKey = null;
          schemaKeys.forEach((schemaKey) => {if (schema[schemaKey].root) rootSchemaKey = schemaKey;});

          if (!rootSchemaKey) return 0;
          return dfs(rootSchemaKey, 1);

        }

        schemaKeys.forEach((schemaKey) => {

          flattenedArray.push({
            _id: generateObjectId(),
            type: 'header',
            schemaKey,
            schema: schema[schemaKey],
            fieldPositionSchema: fieldPositionSchema?.[schemaKey] || {},
            parentId: null,
            depth: getSchemaDepth(schema, schemaKey) || 0,
            nestedPath: '',

            /**
             * layoutType and layoutKey are used to handle 
             * PDF Rendering page preaks and measurements.
             */
            layoutType: 'header',
            layoutKey: `${schemaKey}-header`,

          });
        });

        return flattenedArray;

      }
    );

    this._getVisibleSchemaIdsFromFlattenedRows = createSelector(
      (state) => state.flattenedRows,
      (flattenedRows) => {

        const uniqueKeys = [];

        flattenedRows.forEach(item => {
          if (!item.schemaKey) return;
          if (!uniqueKeys.includes(item.schemaKey)) {
              uniqueKeys.push(item.schemaKey);
          }
        });

        return uniqueKeys;

      }
    );
  }

  /**
   * Get per-schema styles from fieldPositionSchema with fallback to root props.
   * Only applies per-schema overrides for non-root schemas.
   * 
   * @param {string} schemaKey - The schema key to get styles for
   * @returns {Object} - Object containing resolved styles for the schema
   */
  getSchemaStyles = (schemaKey) => {
    const { fieldPositionSchema, schema } = this.props;
    
    // Only apply per-schema overrides for non-root schemas
    if (schema?.[schemaKey]?.root) {
      return {};
    }
    
    const schemaStyles = fieldPositionSchema?.[schemaKey]?.styles || {};
    return schemaStyles;
  };

  componentDidUpdate() {

    const { allColumnWidthsLoaded, rowHeights } = this.state;

    const { 
      onMeasureCompleted,
      measure,
      measuredOn,
      updatedOn,
      fieldMode, 
      value, 
      type, 
      displayType, 
      schema, 
      fieldPositionSchema, 
      disabled, 
      fieldId, 
      fieldIdentifier, 
      fieldPositionId,
      view,
      docFields
    } = this.props;

    if (!allColumnWidthsLoaded && displayType === FieldDisplayTypes.original && schema) {
      const allSchemasEmpty = Object.keys(schema).every((schemaKey) => {
        const targetSchema = schema[schemaKey];
        const targetFPSchema = fieldPositionSchema?.[schemaKey] || {};
        const filtered = filterHiddenTableColumnsSelector({
          columns: targetSchema.tableColumns,
          fieldPositionColumns: targetFPSchema.tableColumns || {},
          view,
          mode: fieldMode,
          docFields
        });
        return filtered.length === 0;
      });
      if (allSchemasEmpty) {
        this.setState({ allColumnWidthsLoaded: true });
      }
    }

    /**
     * 1. We only require columnWidths when displayType is original (table display type)
     * 2. We do not require columnWidths when displayType is inputGroup or rte because they are not rendered with columns.
     */
    const columnsReadyForMeasurement = (allColumnWidthsLoaded && displayType === FieldDisplayTypes.original) || (displayType === FieldDisplayTypes.inputGroup) || (displayType === FieldDisplayTypes.rte);

    /**
     * Handle field measurements
     * 1. Measure for PDF Generation. This requires all the individual rows to be measured.
     * 2. Measure for edit, fill, & readonly modes. This requires the container table height to be measured.
     */

    if (onMeasureCompleted && measure && this.containerRef && columnsReadyForMeasurement) {

      const formattedRows = this.getFlattenedRowsHelper();

      if (displayType === FieldDisplayTypes.rte && fieldMode === FieldModes.pdf) {
       
        let needsUpdate = false;
       
        formattedRows.forEach(item => {
       
          if (item.type === 'row' && typeof this.measuredRows[item.layoutKey] !== 'number') {
          
            const targetFPSchema = fieldPositionSchema?.[item.schemaKey] || {};
            const rteColumns = targetFPSchema.settings || [];
            const targetSchema = schema[item.schemaKey];
           
            if (!isRowPopulatedForRte(rteColumns, item, targetSchema?.tableColumns)) {
              this.measuredRows[item.layoutKey] = 0;
              needsUpdate = true;
            }
          
          }
      
          if (item.type === 'header' && typeof this.measuredRows[item.layoutKey] !== 'number') {
            const targetFPSchema = fieldPositionSchema?.[item.schemaKey] || {};
            const rteColumns = targetFPSchema.settings || [];
            if (rteColumns.length === 0) {
              this.measuredRows[item.layoutKey] = 0;
              needsUpdate = true;
            }
          }

        });

        if (needsUpdate) {
        
          this.setState({ rowHeights: { ...rowHeights, ...this.measuredRows } });
        
          return;
        }

      }

      const measuredTotal = Object.keys(rowHeights).length;

      //A remeasure is required if the measuredOn time is less than the updatedOn time. By measuredOn time 
      //and updatedOn time we can determine if a remeasure is required due to a field change.
      const remeasureRequired = measuredOn && updatedOn && (measuredOn < updatedOn);

      //1. Handle initial field measurement when all rows are measured
      if (measuredTotal === formattedRows.length && !measuredOn) {
        this.handleMeasure();
      }
      //2. Measure for edit, fill, & readonly modes. 
      //IMPORTANT NOTE: Do not remove the pdf fieldMode check. This is to prevent the field from being 
      //measured before all rows have properly been measured when it is in pdf mode.
      else if (fieldMode !== FieldModes.pdf && remeasureRequired && measuredTotal === formattedRows.length) {
        this.handleMeasure();
      }

    }

  }

  handleMeasure = () => {

    const { 
      fieldId, 
      fieldIdentifier, 
      fieldPositionId, 
      onMeasureCompleted, 
      type, 
      displayType, 
      value, 
      schema, 
      fieldPositionSchema, 
      disabled, 
      fieldMode,
      view,
      docFields
    } = this.props;
    const { rowHeights } = this.state;

    const formattedRows = this.getFlattenedRowsHelper();

    requestAnimationFrame(() => {

      if (typeof this.containerRef?.getBoundingClientRect().height === 'number') {

        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, {
          layouts: formattedRows.map((row) => ({
            type: row.layoutType,
            key: row.layoutKey,
            header: row.header,
            height: rowHeights[row.layoutKey],
          })),
          height: this.containerRef.getBoundingClientRect().height,
          measuredOn: new Date().getTime()
        });


      }

    });

  }

  render() {

    const { allColumnWidthsLoaded } = this.state;

    const {
      fieldId,
      fieldMode,
      disabled,

      fieldPositionId,

      measure,
      tipTitle,
      tipDescription,
      tipVisible,
      type,
      displayType,

      title,
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      value,
      schema,
      margin,

      layouts

    } = this.props;


    /**
     * Step 1: Get needed flattened rows for rendering
     */
    let flattenedArray = [];

    //Step 1.1: If displayType is not original we don't need to wait for column widths to be loaded, we can get the flattened array.
    if (displayType !== FieldDisplayTypes.original) {
      flattenedArray = layouts ? this._getFilteredFlattenedRowsSelector({ items: this.getFlattenedRowsHelper(), layouts }) : this.getFlattenedRowsHelper();
    }

    //Step 1.2: If displayType is original and column widths are loaded we can get the flattened array.
    if (displayType === FieldDisplayTypes.original && allColumnWidthsLoaded) {
      flattenedArray = layouts ? this._getFilteredFlattenedRowsSelector({ items: this.getFlattenedRowsHelper(), layouts }) : this.getFlattenedRowsHelper();
    }

    //Step 1.3: If displayType is original and column widths are not loaded we need only get the header rows so that we can wait for column widths to be loaded.
    else if (displayType === FieldDisplayTypes.original && !allColumnWidthsLoaded ) {
      flattenedArray = this.getHeaderRowsSelectorForColumnWidthsCapture();
    }

    const getTooltip = () => {

      if (disabled) return null;

      return (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      );

    };

    return (
      <StyledWrapper
        {...{
          margin,
          measure
        }}
        data-testid={`${fieldPositionId}-nested-table-field`}
        ref={(e) => {
          this.containerRef = e;
        }}
      >
        {getTooltip()}
        <CollectionRenderer
          {...{
            items: flattenedArray,
            itemRenderer: this.itemRenderer,
            virtual: !measure,
          }}
        />
        {this.state.editRowModalOpen && this.state.editRowModalRow && displayType === FieldDisplayTypes.rte && (() => {
          const modalSchemaKey = this.state.editRowModalSchemaKey;
          const modalRow = this.getFlattenedRowsHelper().find((r) => r._id === this.state.editRowModalRow._id && r.type === 'row') || this.state.editRowModalRow;
          const modalNestedPath = modalRow.nestedPath;
          const modalSchema = schema[modalSchemaKey] || {};
          const modalFieldPositionSchema = this.props.fieldPositionSchema?.[modalSchemaKey] || {};
          const modalRteColumns = modalFieldPositionSchema.settings || [];
          const modalTableColumns = modalSchema.tableColumns;
          const modalFieldPositionTableColumns = modalFieldPositionSchema.tableColumns || {};
          const modalIsRoot = modalSchema.root;
          return (
            <TableFieldRteEditRowModal
              isOpen
              rteColumns={modalRteColumns}
              row={modalRow}
              tableColumns={modalTableColumns}
              fieldPositionTableColumns={modalFieldPositionTableColumns}
              fieldMode={fieldMode}
              onRteSettingsBatchUpdate={(pendingChanges) => this.handleRteSettingsBatchUpdate(pendingChanges, modalSchemaKey)}
              onCellChange={(rowId, columnId, cellValue, timezone, signer) => {
                this.handleCellChange(modalNestedPath, modalSchemaKey, rowId, columnId, cellValue, timezone, modalIsRoot, signer);
              }}
              onCellBatchChange={(rowId, cellChanges, timezone, signer) => {
                this.handleCellBatchChange(modalNestedPath, modalSchemaKey, rowId, cellChanges, timezone, modalIsRoot, signer);
              }}
              onCellFileUpload={(rowId, columnId, value, loadingCallback, errorCallback) => {
                this.handleCellFileUpload(modalNestedPath, modalSchemaKey, rowId, columnId, value, loadingCallback, errorCallback);
              }}
              onCellFileBatchUpload={(rowId, columnUploads, loadingCallback, errorCallback, batchedCells, timezone, signer) => {
                this.handleCellFileBatchUpload(modalNestedPath, modalSchemaKey, rowId, columnUploads, loadingCallback, errorCallback, batchedCells, timezone, signer);
              }}
              onCellFileClick={(rowId, columnId, fileObject, fileIndex) => {
                this.handleCellFileClick(modalNestedPath, modalSchemaKey, rowId, columnId, fileObject, fileIndex);
              }}
              onCellFileDelete={(rowId, columnId, fileObject, fileIndex) => {
                this.handleCellFileDelete(modalNestedPath, modalSchemaKey, rowId, columnId, fileObject, fileIndex);
              }}
              onCapture={this.props.onCapture}
              enableSystemFilePicker={this.props.enableSystemFilePicker}
              onClose={this.handleEditRowModalClose}
            />
          );
        })()}
      </StyledWrapper>
    );
  }

  itemRenderer = ({ item, index, onLoadCompleted, items }) => {

    const {
      view,
      width,
      type: fieldType,
      displayType,
      fieldMode,
      fieldId,
      fieldIdentifier,
      fieldPositionId,
      schema,
      fieldPositionSchema,
      disabled,
      title,
      enableSystemFilePicker,
      decorators,
      rowDecorators,
      onDecoratorClick,

      primaryColor,
      dangerColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleFontStyle,
      titleFontWeight,
      titleTextAlign,
      titleTextTransform,
      titleTextDecoration,

      //Column title styles
      columnTitleFontSize,
      columnTitleFontColor,
      columnTitleFontStyle,
      columnTitleFontWeight,
      columnTitleTextAlign,
      columnTitleTextTransform,
      columnTitleTextDecoration,
      columnTitleBackgroundColor,
      columnTitlePadding,
      columnTitleTextOverflow,

      //Styles
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderWidth,
      borderRadius,
      backgroundColor,
      padding,
      margin,

      value,

      onCapture,
    } = this.props;

    const { focused, titleHeight } = this.state;

    const { _id, depth, type, schemaKey, schema: itemSchema, nestedPath, layoutKey, cells, rowIndex, isInvalid, tz } = item;

    const isRootSchema = schema[schemaKey]?.root;
    const schemaRowDecorators = schema[schemaKey]?.rowDecorators || (isRootSchema ? rowDecorators : undefined);
    const enableRowDecorators = !!(schemaRowDecorators && schemaRowDecorators.length > 0)
      && fieldMode !== FieldModes.pdf;

    const decoratorColumnWidth = getDecoratorColumnWidth(this.context?.visibleLimitInRows || 1);

    const isChildPresent = nestedPath && nestedPath.split('.')[1] === 'children';
    let decoratorParentPath;
    if (isChildPresent) {
      decoratorParentPath = generateParentPath(nestedPath);
    }

    const handleRowDecoratorClick = enableRowDecorators && onDecoratorClick
      ? (decorator, rowId) => {
        onDecoratorClick(fieldId, fieldIdentifier, fieldPositionId, decorator.action, rowId, decoratorParentPath);
      }
      : null;

    const childrenSchemaOptions = [];

    if (itemSchema?.children && itemSchema?.children?.length > 0) {
      itemSchema?.children?.forEach((childSchemaId) => {
        const child = schema[childSchemaId];
        if (!child) return;
        childrenSchemaOptions.push({ _id: childSchemaId, value: `Add ${child.title || 'Nested Table'}` });
      });
    }

    const targetFieldPositionSchema = fieldPositionSchema?.[schemaKey] || {}
    const targetFieldPositionSchemaTableColumns = targetFieldPositionSchema.tableColumns || {};

    const isNestedSchemaRow = items[index - 1]?.schemaKey !== schemaKey;

    /**
     * Resolve per-schema styles with fallback to root props.
     * Only non-root schemas can have custom per-schema styles.
     */
    const schemaOverrides = this.getSchemaStyles(schemaKey);

    // Column title styles - per-schema value or root fallback
    const resolvedColumnTitleFontSize = schemaOverrides.columnTitleFontSize ?? columnTitleFontSize;
    const resolvedColumnTitleFontColor = schemaOverrides.columnTitleFontColor ?? columnTitleFontColor;
    const resolvedColumnTitleFontStyle = schemaOverrides.columnTitleFontStyle ?? columnTitleFontStyle;
    const resolvedColumnTitleFontWeight = schemaOverrides.columnTitleFontWeight ?? columnTitleFontWeight;
    const resolvedColumnTitleTextAlign = schemaOverrides.columnTitleTextAlign ?? columnTitleTextAlign;
    const resolvedColumnTitleTextTransform = schemaOverrides.columnTitleTextTransform ?? columnTitleTextTransform;
    const resolvedColumnTitleTextDecoration = schemaOverrides.columnTitleTextDecoration ?? columnTitleTextDecoration;
    const resolvedColumnTitleBackgroundColor = schemaOverrides.columnTitleBackgroundColor ?? columnTitleBackgroundColor;
    const resolvedColumnTitlePadding = schemaOverrides.columnTitlePadding ?? columnTitlePadding;
    const resolvedColumnTitleTextOverflow = schemaOverrides.columnTitleTextOverflow ?? columnTitleTextOverflow;

    // Cell styles - per-schema value or root fallback
    const resolvedFontSize = schemaOverrides.fontSize ?? fontSize;
    const resolvedFontColor = schemaOverrides.fontColor ?? fontColor;
    const resolvedFontStyle = schemaOverrides.fontStyle ?? fontStyle;
    const resolvedFontWeight = schemaOverrides.fontWeight ?? fontWeight;
    const resolvedTextAlign = schemaOverrides.textAlign ?? textAlign;
    const resolvedTextTransform = schemaOverrides.textTransform ?? textTransform;
    const resolvedTextDecoration = schemaOverrides.textDecoration ?? textDecoration;
    const resolvedTextOverflow = schemaOverrides.textOverflow ?? textOverflow;
    const resolvedBackgroundColor = schemaOverrides.backgroundColor ?? backgroundColor;
    const resolvedPadding = schemaOverrides.padding ?? padding;

    // Field/border styles - per-schema value or root fallback
    const resolvedBorderColor = schemaOverrides.borderColor ?? borderColor;
    const resolvedBorderWidth = schemaOverrides.borderWidth ?? borderWidth;
    const resolvedBorderRadius = schemaOverrides.borderRadius ?? borderRadius;

    let itemNode = null;

    if (type === 'fieldTitle') {
      itemNode = (
        <StyledFieldHeader>
          <FieldTitle
            {...{
              title,
              titleDisplay,
              titleFontSize,
              titleFontColor,
              titleTextAlign,
              titleFontStyle,
              titleFontWeight,
              titleTextTransform,
              titleTextDecoration,
              onRef: (e) => {
                this.handleRowLoadCompleted(layoutKey, { height: e.offsetHeight });
              },
            }}
          />
          <FieldDecorators
            decorators={decorators}
            fieldMode={fieldMode}
            fieldId={fieldId}
            fieldIdentifier={fieldIdentifier}
            fieldPositionId={fieldPositionId}
            onDecoratorClick={onDecoratorClick}
          />
        </StyledFieldHeader>
      );
    } else if (type === 'title' && displayType === FieldDisplayTypes.original) {

      itemNode = (
        <TableFieldHeader
          fieldType={fieldType}
          fieldMode={fieldMode}
          key={index}
          columns={[
            {
              title: itemSchema.title,
            }
          ]}
          {...{
            view,
            disabled,

            //Styles (resolved per-schema)
            fontSize: resolvedColumnTitleFontSize,
            fontColor: resolvedColumnTitleFontColor,
            textAlign: resolvedColumnTitleTextAlign,
            fontStyle: resolvedColumnTitleFontStyle,
            fontWeight: resolvedColumnTitleFontWeight,
            textTransform: resolvedColumnTitleTextTransform,
            textDecoration: resolvedColumnTitleTextDecoration,
            textOverflow: resolvedColumnTitleTextOverflow,
            backgroundColor: resolvedColumnTitleBackgroundColor,
            padding: resolvedColumnTitlePadding,
            borderWidth: resolvedBorderWidth,
            borderColor: resolvedBorderColor,
            borderRadius: resolvedBorderRadius,
            onLoadCompleted: (r, measurement) => this.handleRowLoadCompleted(layoutKey, measurement)
          }}
        />
      );

    } else if (fieldType === FieldTypes.collection && displayType === FieldDisplayTypes.original && type === 'header') {
      itemNode = (
        <TableFieldHeader
          view={view}
          fieldType={fieldType}
          fieldMode={fieldMode}
          docFields={this.props.docFields}
          key={index}
          fieldPositionColumns={targetFieldPositionSchemaTableColumns}
          columns={itemSchema.tableColumns}
          enableNumber
          enableMenu={!disabled}
          enableRowDecorators={enableRowDecorators}
          decoratorColumnWidth={decoratorColumnWidth}
          {...{
            disabled,
            //Styles (resolved per-schema)
            fontSize: resolvedColumnTitleFontSize,
            fontColor: resolvedColumnTitleFontColor,
            textAlign: resolvedColumnTitleTextAlign,
            fontStyle: resolvedColumnTitleFontStyle,
            fontWeight: resolvedColumnTitleFontWeight,
            textTransform: resolvedColumnTitleTextTransform,
            textDecoration: resolvedColumnTitleTextDecoration,
            textOverflow: resolvedColumnTitleTextOverflow,
            backgroundColor: resolvedColumnTitleBackgroundColor,
            padding: resolvedColumnTitlePadding,
            borderWidth: resolvedBorderWidth,
            borderColor: resolvedBorderColor,
            borderRadius: resolvedBorderRadius,
            onColumnRef: (columnId, ref) => this.handleColumnRef(schemaKey, columnId, ref),
            onLoadCompleted: (r, measurement) => this.handleRowLoadCompleted(layoutKey, measurement)
          }}
        />
      );
    } else if (fieldType === FieldTypes.collection && displayType === FieldDisplayTypes.original && type === 'row') {

      const displayRequiredIndicators = fieldMode !== FieldModes.pdf && isInvalid;

      itemNode = (
        <TableFieldRow
          view={view}
          width={width}
          key={index}
          fieldType={fieldType}
          fieldMode={fieldMode}
          docFields={this.props.docFields}
          enableNumber
          enableMenu={!disabled}
          enableRowDecorators={enableRowDecorators}
          decoratorColumnWidth={decoratorColumnWidth}
          rowDecorators={schemaRowDecorators}
          onRowDecoratorClick={handleRowDecoratorClick}
          enableTopBorder={isNestedSchemaRow}
          enableSystemFilePicker={enableSystemFilePicker}
          disabled={disabled}
          focused={focused}
          rowIndex={rowIndex}
          fieldPositionColumns={targetFieldPositionSchemaTableColumns}
          columns={itemSchema.tableColumns}
          columnWidths={this.state[`${schemaKey}ColumnWidths`]}
          rowPath={nestedPath}
          row={item}
          onCellFocus={(rowId, columnId, columnIdentifier, columnType, e) => { this.handleCellFocus(nestedPath, rowId, columnId, columnIdentifier, columnType, e); }}
          onCellBlur={(rowId, columnId, columnIdentifier, columnType) => this.handleCellBlur(nestedPath, rowId, columnId, columnIdentifier, columnType)}
          onCellChange={(rowId, columnId, value, timezone, signer) => {
            const isRoot = schema[schemaKey]?.root;

            this.handleCellChange(nestedPath, schemaKey, rowId, columnId, value, timezone, isRoot, signer)

          }}
          onCapture={onCapture}
          onCellFileUpload={(rowId, columnId, value, loadingCallback, errorCallback) => {
            this.handleCellFileUpload(nestedPath, schemaKey, rowId, columnId, value, loadingCallback, errorCallback);
          }}
          onCellFileClick={(rowId, columnId, fileObject, fileIndex) => {
            this.handleCellFileClick(nestedPath, schemaKey, rowId, columnId, fileObject, fileIndex);
          }}
          onCellFileDelete={(rowId, columnId, fileObject, fileIndex) => {
            this.handleCellFileDelete(nestedPath, schemaKey, rowId, columnId, fileObject, fileIndex);
          }}
          rowActions={[
            { _id: 'addBelow', value: 'Insert Below' },
            { _id: 'moveUp', value: 'Move Up' },
            { _id: 'moveDown', value: 'Move Down' },
            { _id: 'delete', value: 'Delete' },
            ...childrenSchemaOptions
          ]}
          onRowAction={(type, rowId, rowIndex, nestedPath) => this.handleRowAction(type, rowId, rowIndex, nestedPath, schemaKey)}
          onLoadCompleted={(r, measurement) => this.handleRowLoadCompleted(layoutKey, measurement)}
          {...{
            //Styles (resolved per-schema)
            primaryColor,
            dangerColor,
            fontSize: resolvedFontSize,
            fontColor: resolvedFontColor,
            fontStyle: resolvedFontStyle,
            fontWeight: resolvedFontWeight,
            textAlign: resolvedTextAlign,
            textTransform: resolvedTextTransform,
            textDecoration: resolvedTextDecoration,
            textOverflow: resolvedTextOverflow,
            borderColor: resolvedBorderColor,
            borderWidth: resolvedBorderWidth,
            borderRadius: resolvedBorderRadius,
            backgroundColor: resolvedBackgroundColor,
            padding: resolvedPadding,
          }}
          displayRequiredIndicators={displayRequiredIndicators}
        />
      );
    } else if (displayType === FieldDisplayTypes.original && type === 'footer') {
      itemNode = (
        <TableFieldFooter
          {...{
            disabled,
            //Styles (resolved per-schema)
            backgroundColor: resolvedColumnTitleBackgroundColor,
            padding: resolvedColumnTitlePadding,
            borderWidth: resolvedBorderWidth,
            borderColor: resolvedBorderColor,
            borderRadius: resolvedBorderRadius,
            onRowCreate: () => this.handleAddRow(nestedPath, schemaKey),
          }}
        />
      );
    } else if (displayType === FieldDisplayTypes.inputGroup && type === 'header') {
      itemNode = (
        <FieldTitle
          key={index}
          title={itemSchema.title}
          {...{
            //Styles (resolved per-schema)
            titleFontSize: resolvedColumnTitleFontSize,
            titleFontColor: resolvedColumnTitleFontColor,
            titleTextAlign: resolvedColumnTitleTextAlign,
            titleFontStyle: resolvedColumnTitleFontStyle,
            titleFontWeight: resolvedColumnTitleFontWeight,
            titleTextTransform: resolvedColumnTitleTextTransform,
            titleTextDecoration: resolvedColumnTitleTextDecoration,
            titleTextOverflow: resolvedColumnTitleTextOverflow,
            onRef: (e) => this.handleRowLoadCompleted(layoutKey, { height: e.offsetHeight })
          }}
        />
      );
    } else if (displayType === FieldDisplayTypes.inputGroup && type === 'row') {

      const displayRequiredIndicators = fieldMode !== FieldModes.pdf && isInvalid;

      itemNode = (
        <InputGroupFieldRow
          view={view}
          key={index}
          docFields={this.props.docFields}
          title={itemSchema.title}
          fieldType={fieldType}
          fieldMode={fieldMode}
          enableMenu={!disabled}
          enableRowDecorators={enableRowDecorators}
          rowDecorators={schemaRowDecorators}
          onRowDecoratorClick={handleRowDecoratorClick}
          enableSystemFilePicker={enableSystemFilePicker}
          disabled={disabled}
          focused={focused}
          rowIndex={rowIndex}
          fieldPositionColumns={targetFieldPositionSchemaTableColumns}
          columns={itemSchema.tableColumns}
          rowPath={nestedPath}
          row={item}
          onCapture={onCapture}
          onCellFocus={(rowId, columnId, columnIdentifier, columnType, e) => this.handleCellFocus(nestedPath, rowId, columnId, columnIdentifier, columnType, e)}
          onCellBlur={(rowId, columnId, columnIdentifier) => this.handleCellBlur(nestedPath, rowId, columnId, columnIdentifier)}
          onCellChange={(rowId, columnId, value, timezone, signer) => {
          
            const isRoot = schema[schemaKey]?.root;

            this.handleCellChange(nestedPath, schemaKey, rowId, columnId, value, timezone, isRoot, signer)
          
          }}
          onCellFileUpload={(rowId, columnId, value, loadingCallback, errorCallback) => {
            this.handleCellFileUpload(nestedPath, schemaKey, rowId, columnId, value, loadingCallback, errorCallback);
          }}
          onCellFileClick={this.handleCellFileClick}
          onCellFileDelete={this.handleCellFileDelete}
          rowActions={[
            { _id: 'addBelow', value: 'Insert Below' },
            { _id: 'moveUp', value: 'Move Up' },
            { _id: 'moveDown', value: 'Move Down' },
            { _id: 'delete', value: 'Delete' },
            ...childrenSchemaOptions
          ]}
          onRowAction={(type, rowId, rowIndex, nestedPath) => this.handleRowAction(type, rowId, rowIndex, nestedPath, schemaKey)}
          onLoadCompleted={(r, measurement) => this.handleRowLoadCompleted(layoutKey, measurement)}
          displayRequiredIndicators={displayRequiredIndicators}
          fieldId={fieldId}
          fieldIdentifier={fieldIdentifier}
          fieldPositionId={fieldPositionId}
          decorators={decorators}
          onDecoratorClick={onDecoratorClick}
          {...{
            //Styles (resolved per-schema)
            primaryColor,
            dangerColor,
            fontSize: resolvedFontSize,
            fontColor: resolvedFontColor,
            fontStyle: resolvedFontStyle,
            fontWeight: resolvedFontWeight,
            textAlign: resolvedTextAlign,
            textTransform: resolvedTextTransform,
            textDecoration: resolvedTextDecoration,
            textOverflow: resolvedTextOverflow,
            borderColor: resolvedBorderColor,
            borderWidth: resolvedBorderWidth,
            borderRadius: resolvedBorderRadius,
            backgroundColor: resolvedBackgroundColor,
            padding: resolvedPadding,

            columnTitleFontSize: resolvedColumnTitleFontSize,
            columnTitleFontColor: resolvedColumnTitleFontColor,
            columnTitleFontStyle: resolvedColumnTitleFontStyle,
            columnTitleFontWeight: resolvedColumnTitleFontWeight,
            columnTitleTextAlign: resolvedColumnTitleTextAlign,
            columnTitleTextTransform: resolvedColumnTitleTextTransform,
            columnTitleTextDecoration: resolvedColumnTitleTextDecoration,
            columnTitleBackgroundColor: resolvedColumnTitleBackgroundColor,
            columnTitlePadding: resolvedColumnTitlePadding,
            columnTitleTextOverflow: resolvedColumnTitleTextOverflow,
          }}
        />
      );
    } else if (displayType === FieldDisplayTypes.inputGroup && type === 'footer') {
      itemNode = (
        <TableFieldFooter
          {...{
            disabled,
            //Styles (resolved per-schema, but keeping hardcoded bg/padding for inputGroup footer)
            backgroundColor: 'tranpsarent',
            padding: 0,
            borderWidth: resolvedBorderWidth,
            borderColor: resolvedBorderColor,
            borderRadius: resolvedBorderRadius,
            onRowCreate: () => this.handleAddRow(nestedPath, schemaKey),
          }}
        />
      );
    } else if (displayType === FieldDisplayTypes.rte && type === 'header') {
      const rteColumns = targetFieldPositionSchema.settings || [];
      if (rteColumns.length === 0 && fieldMode !== FieldModes.edit) {
        itemNode = null;
      } else {
        itemNode = (
          <TableFieldRteHeader
            id={fieldId}
            fieldMode={fieldMode}
            rteColumns={rteColumns}
            tableColumns={itemSchema.tableColumns}
            enableNumber
            enableMenu={fieldMode === FieldModes.fill}
            {...{
              fontSize: resolvedColumnTitleFontSize,
              fontColor: resolvedColumnTitleFontColor,
              fontStyle: resolvedColumnTitleFontStyle,
              fontWeight: resolvedColumnTitleFontWeight,
              textAlign: resolvedColumnTitleTextAlign,
              textTransform: resolvedColumnTitleTextTransform,
              textDecoration: resolvedColumnTitleTextDecoration,
              textOverflow: resolvedColumnTitleTextOverflow,
              backgroundColor: resolvedColumnTitleBackgroundColor,
              padding: resolvedColumnTitlePadding,
              borderWidth: resolvedBorderWidth,
              borderColor: resolvedBorderColor,
              loadCompletedId: 'header',
              onLoadCompleted: (r, measurement) => this.handleRowLoadCompleted(layoutKey, measurement),
            }}
          />
        );
      }
    } else if (displayType === FieldDisplayTypes.rte && type === 'row') {
      const rteColumns = targetFieldPositionSchema.settings || [];
      const rowHasContent = isRowPopulatedForRte(rteColumns, item, itemSchema.tableColumns);
      if (!rowHasContent && fieldMode !== FieldModes.edit) {
        itemNode = null;
      } else {
        itemNode = (
          <TableFieldRteRow
            id={fieldId}
            row={item}
            rowIndex={rowIndex}
            rowPath={nestedPath}
            rteColumns={rteColumns}
            tableColumns={itemSchema.tableColumns}
            fieldPositionTableColumns={targetFieldPositionSchemaTableColumns}
            fieldMode={fieldMode}
            disabled={disabled}
            enableNumber
            enableMenu={fieldMode === FieldModes.fill}
            rowActions={[
              { _id: 'editRow', value: 'Edit Row' },
              { _id: 'moveUp', value: 'Move Up' },
              { _id: 'moveDown', value: 'Move Down' },
            ]}
            onRowAction={(type, rowId, rowIndex, nestedPath) => this.handleRowAction(type, rowId, rowIndex, nestedPath, schemaKey)}
            onLoadCompleted={(r, measurement) => this.handleRowLoadCompleted(layoutKey, measurement)}
            {...{
              primaryColor,
              dangerColor,
              fontSize: resolvedFontSize,
              fontColor: resolvedFontColor,
              fontStyle: resolvedFontStyle,
              fontWeight: resolvedFontWeight,
              textAlign: resolvedTextAlign,
              textTransform: resolvedTextTransform,
              textDecoration: resolvedTextDecoration,
              padding: resolvedPadding,
              borderWidth: resolvedBorderWidth,
              borderColor: resolvedBorderColor,
              backgroundColor: resolvedBackgroundColor,
            }}
          />
        );
      }
    } else if (displayType === FieldDisplayTypes.rte && type === 'footer') {
      itemNode = null;
    }

    return (
      <div key={`${schemaKey}-${index}`} style={{ marginLeft: depth ? depth * 12 : 0 }}>
        {itemNode}
      </div>
    );

  }

  handleRowLoadCompleted = (key, measurement) => {

    const { type, displayType, schema, disabled, value, fieldMode } = this.props;
    const { rowHeights } = this.state;

    const rows = this.getFlattenedRowsHelper();

    if (this.measuredRows[key] && this.measuredRows[key] === measurement.height) return;
    this.measuredRows[key] = measurement.height;

    if (Object.keys(rowHeights).length < 1 && rows.length === Object.keys(this.measuredRows).length) {
      this.setState({ rowHeights: { ...rowHeights, ...this.measuredRows } });
    }

  }

  handleColumnRef = (schemaId, columnId, columnRef) => {

    const { view, schema, fieldPositionSchema } = this.props;

    const targetSchema = this.props.schema[schemaId];

    if (!this.columnWidths[schemaId]) this.columnWidths[schemaId] = {};

    if (this.columnWidths[schemaId][columnId] && this.columnWidths[schemaId][columnId] === columnRef.offsetWidth) return;
    this.columnWidths[schemaId][columnId] = columnRef.offsetWidth;

    const totalSchemaColumnWidthsLoaded = Object.keys(this.columnWidths[schemaId]).length;

    const targetFieldPositionSchema = fieldPositionSchema?.[schemaId] || {}
    const targetFieldPositionSchemaTableColumns = targetFieldPositionSchema.tableColumns || {};

    const targetSchemaFilteredColumns = filterHiddenTableColumnsSelector({
      columns: targetSchema.tableColumns,
      fieldPositionColumns: targetFieldPositionSchemaTableColumns,
      view,
      mode: this.props.fieldMode,
      docFields: this.props.docFields
    });

    /**
     * Trigger new column widths only when all the columns are loaded.
     *
     * Why do we allow totalColumnWidthsLoaded to be greater than formattedColumns.length?
     * This is to account when a column is removed from the table.
     */
    if (totalSchemaColumnWidthsLoaded >= targetSchemaFilteredColumns.length) {

      const flattenedRows = this.getFlattenedRowsHelper();

      /**
       * Why do we only check columnWidths for visible schemas? This is because by default our table
       * only renders schemas if a row is actually added to the field value. This means that if there is no
       * row for a schema added to the field value then it's columns will not be rendered and hence 
       * we will not have columnWidths for that schema.
       */
      const totalVisibleSchemas = this._getVisibleSchemaIdsFromFlattenedRows({ flattenedRows });

      const schemasWithVisibleColumns = totalVisibleSchemas.filter((key) => {
        const s = schema[key];
        if (!s || !s.tableColumns) return false;
        const fp = fieldPositionSchema?.[key] || {};
        const filtered = filterHiddenTableColumnsSelector({
          columns: s.tableColumns,
          fieldPositionColumns: fp.tableColumns || {},
          view,
          mode: this.props.fieldMode,
          docFields: this.props.docFields
        });
        return filtered.length > 0;
      });

      this.setState({ 
        [`${schemaId}ColumnWidths`]: { ...this.columnWidths[schemaId] },
        allColumnWidthsLoaded: Object.keys(this.columnWidths).length >= schemasWithVisibleColumns.length 
      });

    }

  }

  getHeaderRowsSelectorForColumnWidthsCapture = () => {
    const { schema, fieldPositionSchema, fieldMode } = this.props;
    return this._getHeaderRowsSelectorForColumnWidthsCapture({ schema, fieldPositionSchema, fieldMode });
  }

  getFlattenedRowsHelper = () => {
    const { type, displayType, schema, fieldPositionSchema, disabled, value, fieldMode, view, docFields } = this.props;
    return this._getFlattenRowsSelector({ type, displayType, value, schema, fieldPositionSchema, disabled, fieldMode, view, docFields });
  }

  handleRowAction = (type, rowId, rowIndex, nestedPath, schemaKey) => {
    if (type === 'editRow') this.handleEditRowModalOpen(rowId, rowIndex, schemaKey);
    else if (type === 'addBelow') this.handleInsertBelow(nestedPath, schemaKey);
    else if (type === 'duplicate') this.handleDuplicate(nestedPath);
    else if (type === 'moveUp') this.handleMove(nestedPath, 'up', rowId, schemaKey);
    else if (type === 'moveDown') this.handleMove(nestedPath, 'down', rowId, schemaKey);
    else if (type === 'delete') this.handleDelete(nestedPath, schemaKey, rowId);
    else this.handleAddChildSchemaRow(nestedPath, type);
  }

  handleEditRowModalOpen = (rowId, rowIndex, schemaKey) => {
    const { value } = this.props;
    const flattenedRows = this.getFlattenedRowsHelper();
    const row = flattenedRows.find((r) => r._id === rowId && r.type === 'row');
    if (row) this.setState({ editRowModalOpen: true, editRowModalRow: row, editRowModalSchemaKey: schemaKey });
  }

  handleEditRowModalClose = () => {
    this.setState({ editRowModalOpen: false, editRowModalRow: null, editRowModalSchemaKey: null });
  }

  handleRteSettingsBatchUpdate = (pendingChanges, schemaKey) => {
    const { onChange, fieldId, fieldIdentifier, fieldPositionId, fieldPositionSchema } = this.props;
    const data = fieldPositionSchema || {};
    const schemaData = data[schemaKey] || {};
    const currentSettings = schemaData.settings || [];
    const nextSettings = currentSettings.map((s) => ({ ...s }));
    const ids = Object.keys(pendingChanges);
    for (let i = 0; i < ids.length; i++) {
      const index = nextSettings.findIndex((s) => s._id === ids[i]);
      if (index !== -1) {
        nextSettings[index] = { ...nextSettings[index], ...pendingChanges[ids[i]] };
      }
    }
    const nextFieldPositionSchema = {
      ...data,
      [schemaKey]: {
        ...schemaData,
        settings: nextSettings,
      },
    };
    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, { fieldPositionSchema: nextFieldPositionSchema });
  }

  handleCellFocus = (nestedPath, rowId, columnId, columnIdentifier, columnType, e) => {

    const { focused } = this.state;

    const { onFocus, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    const focusedKey = `${nestedPath}_${columnId}`;

    const isChildPresent = nestedPath.split('.')[1] === 'children';

    let parentPath;

    if (nestedPath && isChildPresent) {

      parentPath = generateParentPath(nestedPath);

    }

    /**<---------------------------NOTICE------------------------------>
     * Why do we use columnTypes here to trigger onFocus?
     *
     * We use columnTypes to trigger onFocus only when the focused components of a tableField
     * (ie StyledSelect/StyledTextArea) have been rendered so that when user triggers e.blur
     * within the blurFunction of onFocus event we can the blur these focused components.
     * We will only have access to the focused component's event object when they are rendered.
     *
     * Table cells use two sets of components to display data.
     * 1) If a cell is focused StyledSelect or StyledTextarea is used.
     * 2) If a cell is not focused a simple tableDataCell is used.
     *
     * handleCellFocus will be triggered twice everytime a cell is focused.
     *
     * The First call to handleCellFocus:
     * 1. When a table cell is focused for the first time,  TableDataCell will be clicked triggering
     *    handleCellFocus and the focused cell's rowId and columnId  will be set as the focused in state.
     *    ie: this.setState({ focused: `${rowId}_${columnId}` });
     *
     * 2. We should NOT TRIGGER onFocus during the first call. If we do so, we won't be able to blur
     *    the StyledSelect/StyledTextarea when user triggers e.blur since we would not have access to
     *    the event object associated with StyledSelect/StyledTextarea as they are yet to be rendered.
     *
     * 3. Once focused is set in state StyledTextArea or StyledSelectCell will replace TableDataCell.
     *
     * The Second call to handleCellFocus:
     * 1. Once StyledSelect or StyledTextarea is rendered autoFocus prop on them triggers handleCellFocus again.
     * 2. We will only pass columnType to handleCellFocus now and we SHOULD TRIGGER onFocus during this since
     *    we will be able to blur these focused components using e.target.blur when user triggers e.blur from the
     *    blurFunction due to the availablity of the event object associated with StyledSelect/StyledTextarea
     *
     */

    if (onFocus && columnType) {

      const blurFunction = e && e.blur ? {
        blur: () => {
          e.blur();
        }
      } : {};

      onFocus(fieldId, fieldIdentifier, fieldPositionId, blurFunction, rowId, columnId, columnIdentifier, parentPath);

    }

    if (focusedKey === focused) return;

    this.setState({ focused: focusedKey });

  }

  handleCellBlur = (nestedPath, rowId, columnId, columnIdentifier) => {

    const { onBlur, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    this.setState({ focused: null });

    const isChildPresent = nestedPath.split('.')[1] === 'children';

    let parentPath;

    if (nestedPath && isChildPresent) {

      parentPath = generateParentPath(nestedPath);

    }

    if (onBlur) {
      //Row id appears as undefined, commetned out for now
      onBlur(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, columnId, columnIdentifier, parentPath);

    }

  }

  handleMove = (nestedPath, direction, rowId, schemaKey) => {

    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange } = this.props;

    const { newData, targetRowIndex } = moveNestedItem(value, nestedPath, direction);

    const isChildPresent = nestedPath.split('.')[1] === 'children';

    let parentPath;
    let collectionFieldParams = null;

    if (nestedPath && isChildPresent) {

      parentPath = generateParentPath(nestedPath, true);

    }

    collectionFieldParams = { parentPath: parentPath ?? '', schemaId: schemaKey, rowId, targetRowIndex };

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, newData, null, collectionFieldParams, ChangelogTypes.fieldRowMove);

  }

  handleDelete = (nestedPath, schemaKey, rowId) => {

    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange } = this.props;

    const newData = deleteNestedItem(value, nestedPath);

    let parentPath;
    let collectionFieldParams = null;

    const isChildPresent = nestedPath.split('.')[1] === 'children';

    if (nestedPath && isChildPresent) {

      parentPath = generateParentPath(nestedPath, true);

    }

    collectionFieldParams = { parentPath: parentPath ?? '', schemaId: schemaKey, rowId };

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, newData, null, collectionFieldParams, ChangelogTypes.fieldRowDelete);

  }

  handleInsertBelow = (nestedPath, schemaKey, rowId) => {

    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange, schema } = this.props;

    let cells = {};

    //Add default values from column
    if (schema && schema[schemaKey] && schema[schemaKey].tableColumns) {

      cells = addDefaultValuesFromTableColumnsToCells(schema[schemaKey].tableColumns, cells);

    }

    const newRow = { _id: generateObjectId(), cells: cells, children: {} };

    const isChildPresent = nestedPath.split('.')[1] === 'children';

    let parentPath;
    let collectionFieldParams = null;

    const { newData, targetRowIndex } = insertNestedItem(value, nestedPath, newRow);

    if (nestedPath && isChildPresent) {

      parentPath = generateParentPath(nestedPath, true);

    }

    collectionFieldParams = {
      parentPath: parentPath ?? '',
      schemaId: schemaKey,
      row: newRow,
      targetRowIndex
    };

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, newData, null, collectionFieldParams, ChangelogTypes.fieldRowCreate);

  }

  handleDuplicate = (nestedPath) => {

    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange } = this.props;

    const newData = duplicateNestedItem(value, nestedPath, {});

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, newData);

  }

  handleAddRow = (nestedPath, schemaKey) => {

    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange, schema, schemaId } = this.props;

    const eventType = ChangelogTypes.fieldUpdate;

    let cells = {};

    if (schema && schema[schemaKey] && schema[schemaKey].tableColumns) {

      cells = addDefaultValuesFromTableColumnsToCells(schema[schemaKey].tableColumns, cells);

    }

    const newRow = { _id: generateObjectId(), cells, children: {} };

    const { newData, targetRowIndex } = appendNestedItem(value, nestedPath, newRow);

    const isChildPresent = nestedPath.split('.')[1] === 'children';

    let parentPath;

    let collectionFieldParams = null;

    if (nestedPath && isChildPresent) {

      parentPath = generateParentPath(nestedPath);

    }

    collectionFieldParams = {
      parentPath: parentPath ?? '',
      schemaId: schemaKey,
      row: newRow,
      targetRowIndex
    };

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, newData, null, collectionFieldParams, ChangelogTypes.fieldRowCreate);

  }

  handleAddChildSchemaRow = (nestedPath, schemaId) => {
    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange, schema } = this.props;

    let cells = {};

    if (schema && schema[schemaId] && schema[schemaId].tableColumns) {

      cells = addDefaultValuesFromTableColumnsToCells(schema[schemaId].tableColumns, cells);

    }

    const newRow = { _id: generateObjectId(), cells, children: {} };

    const newData = insertIntoNestedChildrenValue(value, nestedPath, schemaId, { _id: generateObjectId(), cells, children: {} });

    const keys = nestedPath.split('.').map(k => (isNaN(k) ? k : Number(k)));
    const parentItem = keys.reduce((acc, key) => acc[key], newData);
    const targetRowIndex = parentItem.children?.[schemaId]?.value?.length - 1 || 0;

    const parentPath = generateParentPath(nestedPath);

    const collectionFieldParams = {
      parentPath: `${parentPath}.${schemaId}`,
      schemaId,
      row: newRow,
      targetRowIndex
    };


    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, newData, null, collectionFieldParams, ChangelogTypes.fieldRowCreate);

  }

  handleCellChange = (nestedPath, schemaKey, rowId, columnId, cellValue, timezone, isRoot, signer) => {


    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange } = this.props;

    const newData = updateNestedItemCells(value, nestedPath, columnId, cellValue, timezone);


    const isChildPresent = nestedPath.split('.')[1] === 'children';

    let parentPath;
    let collectionFieldParams = null;

    const updatedRow = {
      _id: rowId,
      cells: {
        [columnId]: cellValue
      }
    };

    if(timezone && !updatedRow.tz ) updatedRow.tz = timezone;
    if (signer) updatedRow.signer = signer;


    if (nestedPath && isChildPresent) {

      parentPath = generateParentPath(nestedPath, true);

    }

    collectionFieldParams = {
      parentPath: parentPath ?? '',
      schemaId: schemaKey,
      rowId,
      row: updatedRow
    };


    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, newData, null, collectionFieldParams, ChangelogTypes.fieldRowUpdate);

  }

  handleCellBatchChange = (nestedPath, schemaKey, rowId, cellChanges, timezone, isRoot, signer) => {

    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange } = this.props;

    const newData = updateNestedItemCellsBatch(value, nestedPath, cellChanges, timezone);

    const isChildPresent = nestedPath.split('.')[1] === 'children';

    let parentPath;

    const updatedRow = {
      _id: rowId,
      cells: { ...cellChanges }
    };

    if (timezone && !updatedRow.tz) updatedRow.tz = timezone;
    if (signer) updatedRow.signer = signer;

    if (nestedPath && isChildPresent) {
      parentPath = generateParentPath(nestedPath, true);
    }

    const collectionFieldParams = {
      parentPath: parentPath ?? '',
      schemaId: schemaKey,
      rowId,
      row: updatedRow
    };

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, newData, null, collectionFieldParams, ChangelogTypes.fieldRowUpdate);

  }

  handleCellFileClick = (nestedPath, schemaId, rowId, columnId, fileObject, fileIndex) => {

    const { onFileClick, fieldId, fieldIdentifier, fieldPositionId, value } = this.props;

    const parentPath = generateParentPath(nestedPath, true);
    if (onFileClick) onFileClick(fieldId, fieldIdentifier, fieldPositionId, fileObject, fileIndex, { rowId, columnId, parentPath, schemaId});

  }

  handleCellFileDelete = (nestedPath, schemaId, rowId, columnId, fileObject, fileIndex) => {

    const { onFileDelete, fieldId, fieldIdentifier, fieldPositionId, value } = this.props;

    const parentPath = generateParentPath(nestedPath, true);
    if (onFileDelete) onFileDelete(fieldId, fieldIdentifier, fieldPositionId, fileObject, fileIndex, { rowId, columnId, parentPath, schemaId});

  }

  handleCellFileUpload = async (nestedPath, schemaKey, rowId, columnId, cellValue, loadingCallback, errorCallback) => {

    const { fieldId, fieldIdentifier, fieldPositionId, onChange, onUploadAsync, value, documentId, documentIdentifier, fileId, pageId, config, schema } = this.props;

    const { onFileUploadAsync } = config;
    const uploadAsyncHandler = onFileUploadAsync || onUploadAsync;

    let uploadResult;
    let formattedUrlObjects;

    const nestedItem = getNestedItem(value, nestedPath);

   // Determine if column allows multiple files
    const tableColumns = schema[schemaKey]?.tableColumns || [];
    const targetColumn = tableColumns.find((col) => col._id === columnId);
    const isMulti = targetColumn?.multi || false;

    try {
      loadingCallback(true);

      uploadResult = await uploadAsyncHandler(
        {
          target: ChangelogTypes.fieldUpdate,
          _id: documentId,
          identifier: documentIdentifier,
          fileId,
          pageId,
          fieldId,
          fieldIdentifier,
          fieldPositionId,
          multi: isMulti,
          columnId,
          rowId,
          parentPath: generateParentPath(nestedPath, true),
          schemaId: schemaKey,
        },
        cellValue,
      );

      const urlObjects = Array.isArray(uploadResult)
        ? uploadResult
        : [uploadResult];

      formattedUrlObjects = urlObjects.map((urlObject) => {
        const { url, fileName, filePath } = urlObject;

        return { _id: generateObjectId(), url, fileName, filePath };
      });

      loadingCallback(false);
    } catch (error) {
      if (errorCallback) errorCallback(true);
    }

    if (
      !formattedUrlObjects ||
        (formattedUrlObjects && formattedUrlObjects.length === 0) ||
        !formattedUrlObjects[0].url
    ) return;

    let nextUrlObjects = [...formattedUrlObjects];

    if (nestedItem && nestedItem.cells) {

      const existingValue = nestedItem.cells[columnId];

      if (isMulti) {
        nextUrlObjects = Array.isArray(existingValue) ? [...existingValue, ...nextUrlObjects] : nextUrlObjects;
      } else {
        nextUrlObjects = [nextUrlObjects[0]];
      }

    }

    const newData = updateNestedItemCells(value, nestedPath, columnId, nextUrlObjects);

    const isChildPresent = nestedPath.split('.')[1] === 'children';

    let parentPath;
    let collectionFieldParams = null;

    const updatedRow = {
      _id: rowId,
      cells: {
        [columnId]: nextUrlObjects
      }
    };

    if (nestedPath && isChildPresent) {

      parentPath = generateParentPath(nestedPath, true);

    }

    collectionFieldParams = {
      parentPath: parentPath ?? '',
      schemaKey,
      rowId,
      row: updatedRow
    };

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, newData, null, collectionFieldParams, ChangelogTypes.fieldRowUpdate);

  }

  /**
   * Uploads files for multiple image columns in a single row sequentially,
   * then applies all resulting cell changes in one onChange call.
   * @param {Object} columnUploads - Map of columnId -> File[]
   */
  handleCellFileBatchUpload = async (nestedPath, schemaKey, rowId, columnUploads, loadingCallback, errorCallback, batchedCells, timezone, signer) => {

    const { fieldId, fieldIdentifier, fieldPositionId, onChange, onUploadAsync, value, documentId, documentIdentifier, fileId, pageId, config, schema } = this.props;

    const { onFileUploadAsync } = config;
    const uploadAsyncHandler = onFileUploadAsync || onUploadAsync;

    const tableColumns = schema[schemaKey]?.tableColumns || [];
    const columnIds = Object.keys(columnUploads);
    const cellChanges = {};

    if (batchedCells && typeof batchedCells === 'object') {
      Object.assign(cellChanges, batchedCells);
    }

    const nestedItem = getNestedItem(value, nestedPath);

    try {

      loadingCallback(true);

      for (let i = 0; i < columnIds.length; i++) {

        const columnId = columnIds[i];
        const uploadEntry = columnUploads[columnId];
        const fileUploads = uploadEntry.files || uploadEntry;
        const baseExistingValue = uploadEntry.existingValue;

        const targetColumn = tableColumns.find((col) => col._id === columnId);
        const isMulti = targetColumn?.multi || false;

        const uploadResult = await uploadAsyncHandler(
          {
            target: ChangelogTypes.fieldUpdate,
            _id: documentId,
            identifier: documentIdentifier,
            fileId,
            pageId,
            fieldId,
            fieldIdentifier,
            fieldPositionId,
            multi: isMulti,
            columnId,
            rowId,
            parentPath: generateParentPath(nestedPath, true),
            schemaId: schemaKey,
          },
          fileUploads,
        );

        const urlObjects = Array.isArray(uploadResult) ? uploadResult : [uploadResult];

        const formattedUrlObjects = urlObjects.map((urlObject) => {
          const { url, fileName, filePath } = urlObject;
          return { _id: generateObjectId(), url, fileName, filePath };
        });

        if (!formattedUrlObjects || formattedUrlObjects.length === 0 || !formattedUrlObjects[0].url) continue;

        let nextUrlObjects = [...formattedUrlObjects];

        const existingValue = cellChanges[columnId] || (baseExistingValue !== undefined ? baseExistingValue : (nestedItem && nestedItem.cells ? nestedItem.cells[columnId] : undefined));

        if (isMulti) {
          nextUrlObjects = Array.isArray(existingValue) ? [...existingValue, ...nextUrlObjects] : nextUrlObjects;
        } else {
          nextUrlObjects = [nextUrlObjects[0]];
        }

        cellChanges[columnId] = nextUrlObjects;

      }

      loadingCallback(false);

    } catch (error) {
      loadingCallback(false);
      if (errorCallback) errorCallback(true);
    }

    if (Object.keys(cellChanges).length === 0) return;

    const newData = updateNestedItemCellsBatch(value, nestedPath, cellChanges);

    const isChildPresent = nestedPath.split('.')[1] === 'children';

    let parentPath;

    const updatedRow = {
      _id: rowId,
      cells: { ...cellChanges }
    };

    if (signer) updatedRow.signer = signer;

    if (nestedPath && isChildPresent) {
      parentPath = generateParentPath(nestedPath, true);
    }

    const collectionFieldParams = {
      parentPath: parentPath ?? '',
      schemaKey,
      rowId,
      row: updatedRow
    };

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, newData, null, collectionFieldParams, ChangelogTypes.fieldRowUpdate);

  }

}

const addDefaultValuesFromTableColumnsToCells = (tableColumns, cells) => {

  const nextCells = { ...cells };

  tableColumns.forEach((column) => {

    if (column.value) {
      nextCells[column._id] = column.value;
    }

  });

  return nextCells;

};

function generateParentPath(nestedPath, rowAction = false) {
  const parts = nestedPath.split('.');

  // Filter out structural keys you want to ignore
  const filtered = parts.filter((part) => part !== 'children' && part !== 'value');

  // Remove the last element (which is the row index)
  if (rowAction) {
    filtered.pop();
  }

  // Reconstruct the cleaned path
  return filtered.join('.');
}

// === Immutable Path Helpers ===
//
// These replace the JSON.parse(JSON.stringify(data)) deep clone that all 8
// nested-item helpers used to perform. Instead of duplicating the entire value
// tree (megabytes when signatures are present), they shallow-clone only the
// nodes along the path from root to the target, leaving everything else as
// shared references.
//
// Two variants exist because the 8 helpers address the tree in two different
// ways:
//
//   cloneToParentArray(data, path)
//     path ends with an item index (e.g. "0.children.sid.value.1").
//     Pops the last key as targetIndex and clones to the PARENT array.
//     Used by: updateNestedItemCells, updateNestedItemCellsBatch,
//              moveNestedItem, deleteNestedItem, insertNestedItem,
//              duplicateNestedItem.
//
//   cloneToTarget(data, path)
//     path points directly to the target node (array or object).
//     Does NOT pop. Clones the full path.
//     Used by: appendNestedItem (target is an array to push into),
//              insertIntoNestedChildrenValue (target is a row item).

function parsePath(path) {
  if (Array.isArray(path)) return [...path];
  if (!path) return [];
  return path.split('.').map((k) => (isNaN(k) ? k : Number(k)));
}

function cloneAlongPath(data, keys) {
  function cloneStep(current, keyIndex) {
    if (Array.isArray(current)) {
      const newArr = [...current];
      if (keyIndex < keys.length) {
        newArr[keys[keyIndex]] = cloneStep(newArr[keys[keyIndex]], keyIndex + 1);
      }
      return newArr;
    }
    const newObj = { ...current };
    if (keyIndex < keys.length) {
      newObj[keys[keyIndex]] = cloneStep(newObj[keys[keyIndex]], keyIndex + 1);
    }
    return newObj;
  }
  return cloneStep(data, 0);
}

function cloneToParentArray(data, path) {
  const keys = parsePath(path);
  const targetIndex = keys.pop();
  const newData = cloneAlongPath(data, keys);
  const parentArray = keys.length === 0 ? newData : keys.reduce((acc, key) => acc[key], newData);

  if (!Array.isArray(parentArray)) {
    throw new Error('The resolved parent is not an array.');
  }

  return { newData, parentArray, targetIndex };
}

function cloneToTarget(data, path) {
  const keys = parsePath(path);
  if (keys.length === 0) {
    const newData = Array.isArray(data) ? [...data] : { ...data };
    return { newData, target: newData };
  }
  const newData = cloneAlongPath(data, keys);
  const target = keys.reduce((acc, key) => acc[key], newData);
  return { newData, target };
}

/**
 * Updates the cells property of a nested item within its parent array.
 *
 * @param {Object} data - The JSON object.
 * @param {string | Array<string|number>} path - The dot-notated string or array representing the path to the item to update.
 *        For example: "0.children.buildingSchemaId.value.0.children.systemSchemaId.value.0.children.assetSchemaId.value.0.children.deficiencySchemaId.value.1"
 *        indicates the target item in the nested array.
 * @param {Object} newCells - The new cells object to set on the target item.
 * @returns {Object} The modified data object.
 */
function updateNestedItemCells(data, path, columnId, value, timezone) {

  const { newData, parentArray, targetIndex } = cloneToParentArray(data, path);

  if (targetIndex < 0 || targetIndex >= parentArray.length) {
    throw new Error('The target index is out of bounds.');
  }

  const currentCells = parentArray[targetIndex].cells || {};
  const updated = { ...parentArray[targetIndex], cells: { ...currentCells, [columnId]: value } };
  if (timezone && !updated.tz) updated.tz = timezone;
  parentArray[targetIndex] = updated;

  return newData;
}

function updateNestedItemCellsBatch(data, path, cellChanges, timezone) {

  const { newData, parentArray, targetIndex } = cloneToParentArray(data, path);

  if (targetIndex < 0 || targetIndex >= parentArray.length) {
    throw new Error('The target index is out of bounds.');
  }

  const currentCells = parentArray[targetIndex].cells || {};
  const updated = { ...parentArray[targetIndex], cells: { ...currentCells, ...cellChanges } };
  if (timezone && !updated.tz) updated.tz = timezone;
  parentArray[targetIndex] = updated;

  return newData;
}

/**
 * Moves a nested item up or down in its parent array.
 *
 * Example Usage:
 * Suppose you want to move "deficiency2" up in its array. Given your JSON, the path to "deficiency2" might look like:
 *
 * const path = "0.children.buildingSchemaId.value.0.children.systemSchemaId.value.0.children.assetSchemaId.value.0.children.deficiencySchemaId.value.1";
 *
 * Then call:
 * const newData = moveNestedItem(jsonData, path, "up");
 *
 * This will swap the item at index 1 ("deficiency2") with the one at index 0 ("deficiency1") within the deficiencySchemaId.value array.
 * You can similarly use "down" to move the item down one position.
 *
 * How It Works:
 *  * Path Normalization:The function converts the path into an array (if it isn’t one already), ensuring that numbers (array indices) are properly interpreted.
 *  * Traversing to the Parent Array: By removing the last element from the path, the function locates the parent array where the target element lives.
 *  * Swapping Elements: Based on the direction, the function swaps the target element with its neighbor if that swap is valid.
 *
 * @param {Object} data - The JSON object.
 * @param {string | Array<string|number>} path - The dot-notated string or array representing the path to the item.
 *        For example: "0.children.buildingSchemaId.value.0.children.systemSchemaId.value.0.children.assetSchemaId.value.0.children.deficiencySchemaId.value.1"
 * @param {"up"|"down"} direction - The direction to move the item.
 * @returns {Object} The modified data object.
 */
function moveNestedItem(data, path, direction) {

  const { newData, parentArray, targetIndex } = cloneToParentArray(data, path);

  if (direction === 'up') {
    if (targetIndex <= 0) {
      return {
        newData,
        targetRowIndex: 0,
      };
    }
    [parentArray[targetIndex - 1], parentArray[targetIndex]] =
      [parentArray[targetIndex], parentArray[targetIndex - 1]];
  } else if (direction === 'down') {
    if (targetIndex >= parentArray.length - 1) {
      return {
        newData,
        targetRowIndex: targetIndex
      };
    }
    [parentArray[targetIndex], parentArray[targetIndex + 1]] =
      [parentArray[targetIndex + 1], parentArray[targetIndex]];
  } else {
    throw new Error('Direction must be either "up" or "down".');
  }

  return {
    newData,
    targetRowIndex: direction === 'up'
    ? targetIndex - 1
    : targetIndex + 1,
  };
}

/**
 * Deletes a nested item from its parent array.
 *
 * Below is a method that deletes an object from a nested array given a dot‑notated path or an array of keys/indexes.
 * This function locates the parent array by traversing the nested structure and then removes the element at the target
 * index using JavaScript's array splice
 *
 * @param {Object} data - The JSON object.
 * @param {string | Array<string|number>} path - The dot-notated string or array representing the path to the item.
 *        For example: "0.children.buildingSchemaId.value.0.children.systemSchemaId.value.0.children.assetSchemaId.value.0.children.deficiencySchemaId.value.1"
 * @returns {Object} The modified data object.
 */
function deleteNestedItem(data, path) {

  const { newData, parentArray, targetIndex } = cloneToParentArray(data, path);

  if (targetIndex < 0 || targetIndex >= parentArray.length) {
    throw new Error('The target index is out of bounds.');
  }

  parentArray.splice(targetIndex, 1);

  return newData;
}

/**
 * Inserts a nested item into its parent array at the specified index.
 *
 * @param {Object} data - The JSON object.
 * @param {string | Array<string|number>} path - The dot-notated string or array representing the path where the item should be inserted.
 *        For example: "0.children.buildingSchemaId.value.0.children.systemSchemaId.value.0.children.assetSchemaId.value.0.children.deficiencySchemaId.value.1"
 *        This indicates that the new item will be inserted at index 1 of the target array.
 * @param {Object} newItem - The new object to insert.
 * @returns {Object} The modified data object.
 */
function insertNestedItem(data, path, newItem) {

  const { newData, parentArray, targetIndex: insertIndex } = cloneToParentArray(data, path);

  if (insertIndex < 0 || insertIndex > parentArray.length) {
    throw new Error('The target index is out of bounds.');
  }

  parentArray.splice(insertIndex + 1, 0, newItem);
  return {
    newData,
    targetRowIndex: insertIndex + 1,
  };
}

/**
 * Appends a nested child object to the bottom of a parent's array.
 *
 * @param {Object} value - The JSON object.
 * @param {string | Array<string|number>} path - The dot-notated string or array representing the path to the nested array.
 *        For example: "0.children.buildingSchemaId.value.0.children.systemSchemaId.value.0.children.assetSchemaId.value.0.children.deficiencySchemaId.value"
 *        This indicates the target "value" array.
 * @param {Object} newChild - The new child object to append.
 * @returns {Object} The modified data object.
 */
function appendNestedItem(value, path, newChild) {

  let newData;
  let targetArray;

  if (path === '0' || !path) {
    newData = [...value];
    targetArray = newData;
  } else {
    const result = cloneToTarget(value, path);
    newData = result.newData;
    targetArray = result.target;
  }

  if (!Array.isArray(targetArray)) {
    throw new Error('The resolved target is not an array.');
  }

  const targetRowIndex = targetArray.length !== 0 ? targetArray.length - 1 : 0;

  targetArray.push(newChild);
  return {
    newData,
    targetRowIndex: targetRowIndex + 1,
  };
}

/**
 * Duplicates a nested item within its parent array.
 *
 * @param {Object} data - The JSON object.
 * @param {string | Array<string|number>} path - The dot-notated string or array representing the path to the item to duplicate.
 *        For example: "0.children.buildingSchemaId.value.0.children.systemSchemaId.value.0.children.assetSchemaId.value.0.children.deficiencySchemaId.value.1"
 *        indicates the target item in the nested array.
 * @param {Object} overwrites - The JSON properties to overwrite on the duplicated object.
 * @returns {Object} The modified data object.
 */
function duplicateNestedItem(data, path, overwrites) {

  const { newData, parentArray, targetIndex } = cloneToParentArray(data, path);

  if (targetIndex < 0 || targetIndex >= parentArray.length) {
    throw new Error('The target index is out of bounds.');
  }

  // Deep-clone ONLY the single target item to isolate the duplicate's
  // nested cells/children/signatures from the original row.
  const duplicateItem = {
    ...JSON.parse(JSON.stringify(parentArray[targetIndex])),
    ...overwrites
  };

  parentArray.splice(targetIndex + 1, 0, duplicateItem);

  return newData;
}

/**
 * Inserts a new child object into the children[schemaId].value array of a nested item.
 *
 * @param {Object} data - The JSON object.
 * @param {string | Array<string|number>} path - The dot-notated string or array representing the path to the target object.
 *        For example: "0.children.buildingSchemaId.value.0.children.systemSchemaId.value.0.children.assetSchemaId.value.0"
 *        points to the asset object where you want to insert a new child.
 * @param {string} schemaId - The key within the children object whose value is expected to be an object with a value array.
 * @param {Object} newChild - The new child object to insert.
 * @returns {Object} The modified data object.
 */
function insertIntoNestedChildrenValue(value, path, schemaId, newChild) {

  const { newData, target: targetItem } = cloneToTarget(value, path);

  if (typeof targetItem !== 'object' || targetItem === null) {
    throw new Error('The resolved target is not a valid object.');
  }

  // Shallow-clone the children chain so mutations stay isolated from
  // the original tree. cloneToTarget only clones nodes ON the path;
  // children/children[schemaId]/value are siblings that need explicit copies.
  if (!targetItem.hasOwnProperty('children')) {
    targetItem.children = {};
  } else {
    targetItem.children = { ...targetItem.children };
  }

  if (!targetItem.children.hasOwnProperty(schemaId)) {
    targetItem.children[schemaId] = { value: [] };
  } else {
    targetItem.children[schemaId] = {
      ...targetItem.children[schemaId],
      value: [...(targetItem.children[schemaId].value || [])]
    };
  }

  if (!Array.isArray(targetItem.children[schemaId].value)) {
    throw new Error(`targetItem.children[${schemaId}].value is not an array.`);
  }

  targetItem.children[schemaId].value.push(newChild);

  return newData;
}

export default CollectionField;
